import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { SiteHeader } from "../components/SiteHeader";
import { StatusBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { api, getAdminToken, setAdminToken } from "../lib/api";
import { Loader2, LogOut, RefreshCcw, Inbox, Ticket, Wand2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminDashboardPage() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [promoReport, setPromoReport] = useState(null);
  const [promoBusy, setPromoBusy] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/submissions");
      setSubmissions(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        setAdminToken(null);
        navigate("/admin/login");
      } else {
        toast.error("Failed to load submissions");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!getAdminToken()) {
      navigate("/admin/login");
      return;
    }
    load();
  }, [load, navigate]);

  const logout = () => {
    setAdminToken(null);
    navigate("/admin/login");
  };

  const loadPromoStatus = useCallback(async () => {
    setPromoBusy(true);
    try {
      const { data } = await api.get("/admin/promo-code");
      setPromoReport(data);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      setPromoReport({ ok: false, error: String(detail) });
      toast.error("Could not load promo code status");
    } finally {
      setPromoBusy(false);
    }
  }, []);

  const ensurePromoCode = useCallback(async () => {
    setPromoBusy(true);
    try {
      const { data } = await api.post("/admin/promo-code/ensure");
      setPromoReport(data);
      toast.success("Provisioning run finished — see report below");
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      setPromoReport({ ok: false, error: String(detail) });
      toast.error("Provisioning failed — see report below");
    } finally {
      setPromoBusy(false);
    }
  }, []);

  useEffect(() => {
    // Auto-load the promo status the first time the page mounts.
    if (getAdminToken()) loadPromoStatus();
  }, [loadPromoStatus]);

  const selected = submissions.find((s) => s.id === selectedId);

  return (
    <div className="min-h-screen bg-slate-50/40">
      <SiteHeader />
      <main className="max-w-6xl mx-auto px-6 py-10" data-testid="admin-dashboard-main">
        <div className="flex items-end justify-between gap-4 mb-8">
          <div>
            <p className="eyebrow mb-2">Admin</p>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              Swing submissions
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {submissions.length} total · anonymous AI analyses.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="refresh-dashboard-button"
              variant="outline"
              onClick={load}
              className="gap-2"
            >
              <RefreshCcw className="w-4 h-4" /> Refresh
            </Button>
            <Button
              data-testid="admin-logout-button"
              variant="ghost"
              onClick={logout}
              className="gap-2 text-slate-500"
            >
              <LogOut className="w-4 h-4" /> Log out
            </Button>
          </div>
        </div>

        {/* MALCOMTEST promo code diagnostic + one-click repair */}
        <Card
          data-testid="promo-code-card"
          className="border-slate-200 mb-6 p-5"
        >
          <div className="flex items-start justify-between gap-4 mb-3">
            <div className="flex items-start gap-3">
              <span className="w-9 h-9 rounded-md bg-emerald-100 text-[#065F46] flex items-center justify-center shrink-0">
                <Ticket className="w-4 h-4" />
              </span>
              <div>
                <p className="text-sm font-semibold text-slate-900">Test promotion code (MALCOMTEST)</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Diagnoses whether the 100%-off code is live-mode active in the Stripe account this
                  server is talking to. Use &ldquo;Re-provision&rdquo; if the code is missing or inactive.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                data-testid="promo-refresh-button"
                variant="outline"
                size="sm"
                onClick={loadPromoStatus}
                disabled={promoBusy}
                className="gap-2"
              >
                {promoBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5" />}
                Refresh
              </Button>
              <Button
                data-testid="promo-ensure-button"
                size="sm"
                onClick={ensurePromoCode}
                disabled={promoBusy}
                className="gap-2 bg-[#065F46] hover:bg-[#047857] text-white"
              >
                <Wand2 className="w-3.5 h-3.5" />
                Re-provision
              </Button>
            </div>
          </div>

          {promoReport && (
            <div
              data-testid="promo-report"
              className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3"
            >
              <div className="p-3 rounded-md border border-slate-200 bg-slate-50/60">
                <p className="eyebrow text-slate-500 mb-1">Stripe mode</p>
                <p
                  data-testid="promo-report-mode"
                  className={`text-sm font-semibold ${
                    promoReport.server_stripe_mode === "live" ? "text-[#065F46]" : "text-[#B45309]"
                  }`}
                >
                  {promoReport.server_stripe_mode || "unknown"}
                </p>
                {promoReport.key_prefix && (
                  <p className="text-[11px] text-slate-500 mt-1 font-mono">key {promoReport.key_prefix}</p>
                )}
              </div>
              <div className="p-3 rounded-md border border-slate-200 bg-slate-50/60">
                <p className="eyebrow text-slate-500 mb-1">Coupon</p>
                <p className={`text-sm font-semibold ${promoReport.coupon?.found ? "text-[#065F46]" : "text-rose-700"}`}>
                  {promoReport.coupon?.found ? "Found & valid" : "Missing"}
                </p>
                {promoReport.coupon?.percent_off != null && (
                  <p className="text-[11px] text-slate-500 mt-1">{promoReport.coupon.percent_off}% off · {promoReport.coupon.duration}</p>
                )}
              </div>
              <div className="p-3 rounded-md border border-slate-200 bg-slate-50/60">
                <p className="eyebrow text-slate-500 mb-1">Promotion code</p>
                <p
                  data-testid="promo-report-code-status"
                  className={`text-sm font-semibold ${
                    promoReport.promotion_code?.found && promoReport.promotion_code?.active
                      ? "text-[#065F46]"
                      : "text-rose-700"
                  }`}
                >
                  {promoReport.promotion_code?.found
                    ? promoReport.promotion_code.active ? "Active" : "Inactive"
                    : "Missing"}
                </p>
                {promoReport.promotion_code?.times_redeemed != null && (
                  <p className="text-[11px] text-slate-500 mt-1">Redeemed {promoReport.promotion_code.times_redeemed}×</p>
                )}
              </div>
              {promoReport.diagnosis && (
                <div className="sm:col-span-3 p-3 rounded-md border border-amber-200 bg-amber-50 text-xs text-slate-800 leading-relaxed" data-testid="promo-report-diagnosis">
                  <strong>Diagnosis:</strong> {promoReport.diagnosis}
                </div>
              )}
              {promoReport.reason && (
                <div className="sm:col-span-3 p-3 rounded-md border border-amber-200 bg-amber-50 text-xs text-slate-800 leading-relaxed" data-testid="promo-report-reason">
                  <strong>Reason:</strong> {promoReport.reason}
                </div>
              )}
              {promoReport.error && (
                <div className="sm:col-span-3 p-3 rounded-md border border-rose-200 bg-rose-50 text-xs text-rose-800 font-mono break-all" data-testid="promo-report-error">
                  {promoReport.error}
                </div>
              )}
              {promoReport.coupon?.error && !promoReport.coupon?.found && (
                <div className="sm:col-span-3 p-3 rounded-md border border-rose-200 bg-rose-50 text-xs text-rose-800 font-mono break-all">
                  {promoReport.coupon.error}
                </div>
              )}
            </div>
          )}
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <Card
            className="border-slate-200 lg:col-span-3 overflow-hidden"
            data-testid="submissions-list-card"
          >
            {loading ? (
              <div className="p-16 flex items-center justify-center">
                <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
              </div>
            ) : submissions.length === 0 ? (
              <div className="p-16 text-center" data-testid="empty-submissions">
                <Inbox className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                <p className="text-sm text-slate-500">No submissions yet.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="eyebrow">Submission</TableHead>
                    <TableHead className="eyebrow">Submitted</TableHead>
                    <TableHead className="eyebrow">Payment</TableHead>
                    <TableHead className="eyebrow">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {submissions.map((s) => (
                    <TableRow
                      key={s.id}
                      data-testid={`submission-row-${s.id}`}
                      onClick={() => setSelectedId(s.id)}
                      className={`cursor-pointer ${
                        selectedId === s.id ? "bg-emerald-50/60" : ""
                      }`}
                    >
                      <TableCell className="font-mono text-xs text-slate-500 max-w-[180px] truncate">
                        {s.id.slice(0, 8)}…
                      </TableCell>
                      <TableCell className="text-slate-500 text-sm">
                        {new Date(s.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-slate-700 text-xs uppercase tracking-wider">
                        {s.payment_status || "free"}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={s.status} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>

          <div className="lg:col-span-2">
            {selected ? (
              <Card className="border-slate-200 p-5 sticky top-24" data-testid="submission-detail-card">
                <p className="eyebrow mb-2">Detail</p>
                <p className="text-xs text-slate-400 mb-4">
                  {new Date(selected.created_at).toLocaleString()}
                </p>

                {selected.user_input && (
                  <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-md" data-testid="admin-user-input">
                    <p className="eyebrow mb-1">User&apos;s problem</p>
                    <p className="text-xs text-slate-700 whitespace-pre-wrap leading-relaxed">
                      {selected.user_input}
                    </p>
                  </div>
                )}

                {selected.free_preview && (
                  <div className="mb-4 p-3 bg-emerald-50/60 border border-emerald-100 rounded-md">
                    <p className="eyebrow mb-1 text-[#065F46]">Free preview</p>
                    <p className="text-xs text-slate-700">Score: <b>{selected.free_preview.swing_score}/100</b></p>
                    <p className="text-xs text-slate-700">Main fault: {selected.free_preview.main_fault || "—"}</p>
                    <p className="text-xs text-slate-700">Impact: {selected.free_preview.ball_flight_impact || "—"}</p>
                  </div>
                )}

                {selected.premium_analysis && (
                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-md">
                    <p className="eyebrow mb-1">Premium analysis</p>
                    <p className="text-xs text-slate-700 whitespace-pre-wrap leading-relaxed">
                      {selected.premium_analysis.complete_analysis || "—"}
                    </p>
                  </div>
                )}
              </Card>
            ) : (
              <Card className="border-slate-200 border-dashed p-10 text-center" data-testid="no-selection-card">
                <p className="text-sm text-slate-500">Select a submission to view detail.</p>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
